import React from 'react';
import { FOCUS_RING } from './focusRing';

interface RadioProps {
    checked: boolean;
    /** Optional when `presentational` — a presentational radio is a visual indicator only. */
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
    size?: number;
    /**
     * Render a visual-only indicator (a non-interactive, non-focusable `<span>`,
     * `aria-hidden`) instead of an interactive radio. Use this inside a clickable
     * row/card whose wrapper is the real control, so there's a single tab stop.
     */
    presentational?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
    id?: string;
}

const Radio: React.FC<RadioProps> = ({
    checked,
    onChange,
    disabled = false,
    size = 14,
    presentational = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
    id,
}) => {
    // Keep the dot an even number of pixels so the leftover space (size - dot) is even
    // and the flex-centred dot lands on whole pixels. An odd dot leaves a half-pixel per
    // side, which renders visibly off-centre on 1x / fractionally-scaled displays
    // (e.g. Windows at 125%/150%).
    const dot = 2 * Math.round((size * 0.5) / 2);

    // Border colour lives in Tailwind classes (not inline) so the hover variant can
    // override it — inline styles would always win over a `:hover` rule. A
    // presentational indicator has no interactive hover (the row owns hover).
    const hoverClass =
        disabled || presentational ? '' : ' hover:border-[color:var(--malachite-text-secondary)]';
    const borderClass = checked
        ? 'border-[color:var(--malachite-accent-foreground)]'
        : `border-[color:var(--malachite-text-placeholder)]${hoverClass}`;

    const circleStyle: React.CSSProperties = {
        position: 'relative',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: `${size}px`,
        height: `${size}px`,
        padding: 0,
        borderRadius: '50%',
        borderWidth: '1px',
        borderStyle: 'solid',
        backgroundColor: 'transparent',
        opacity: disabled ? 0.5 : 1,
        transition: 'border-color 150ms ease-in-out',
    };

    const innerDot = checked ? (
        <span
            style={{
                width: `${dot}px`,
                height: `${dot}px`,
                borderRadius: '50%',
                backgroundColor: 'var(--malachite-accent-foreground, rgb(2, 122, 66))',
            }}
        />
    ) : null;

    if (presentational) {
        return (
            <span aria-hidden="true" className={`${borderClass} ${className}`} style={circleStyle}>
                {innerDot}
            </span>
        );
    }

    return (
        <button
            type="button"
            role="radio"
            id={id}
            aria-checked={checked}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            disabled={disabled}
            onClick={() => {
                // Radios don't toggle off on re-click; the parent manages the group.
                if (!checked) onChange?.(true);
            }}
            className={`${FOCUS_RING} ${borderClass} ${className}`}
            style={{ ...circleStyle, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
            {innerDot}
        </button>
    );
};

export default Radio;
export { Radio };
export type { RadioProps };
